<template>
  <b-modal
    id="import-hyperlinks-modal"
    :title="bulkUpload ? `Import Hyperlinks (File ${currentFileIndex + 1} of ${bulkJsonFiles.length})` : 'Import Hyperlinks'"
    centered
    size="lg"
    @ok="handleOk"
    @hide="handleClose"
    @shown="handleModalShown"
  >
    <div
      v-show="isDraggingOver"
      id="drop-area"
      class="drag-overlay"
    >
      <span class="drag-overlay-text">Drag & drop files here</span>
    </div>

    <b-progress
      v-if="bulkUpload"
      :value="currentFileIndex + 1"
      :max="bulkJsonFiles.length"
      show-progress
      class="mb-3"
      height="1rem"
    >
      <b-progress-bar
        :value="currentFileIndex + 1"
        :max="bulkJsonFiles.length"
      >
        {{ Math.round(((currentFileIndex + 1) / bulkJsonFiles.length) * 100) }}%
      </b-progress-bar>
    </b-progress>
    <b-form-row>
      <b-col cols="12">
        <b-form-group
          :label="bulkUpload ? 
            `Current JSON file (${file ? file.name : ''})` : 
            `Current JSON file (${file ? file.name : ''})`"
        >
          <b-form-file
            v-model="file"
            placeholder="Choose JSON file or drop it here..."
            drop-placeholder="Drop file here..."
            accept=".json"
            :disabled="bulkUpload"
            @change="handleFileChange"
          />
        </b-form-group>
      </b-col>
    </b-form-row>

    <!-- Template selection dropdown -->
    <b-form-row class="mt-3">
      <b-col cols="12">
        <b-form-group label="Apply to template:">
          <b-form-select 
            v-model="selectedTemplateId" 
            :options="templateOptions"
            :disabled="!file"
          />
        </b-form-group>
      </b-col>
    </b-form-row>

    <b-row>
      <b-col cols="12">
        <b-table :items="hyperlinks" />
      </b-col>
    </b-row>
    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="handleClose"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        @click="handleOk"
      >
        {{ bulkUpload && currentFileIndex < bulkJsonFiles.length - 1 ? 'Next File' : 'Import' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { importHyperlinksEventBroker, EVENTS } from '@frontend/group/modules/import-hyperlinks/event-broker';
import { mapHyperlinks, MAPPER_TYPES } from '@frontend/group/modules/import-hyperlinks/import-hyperlinks';

export default {
  name: 'ImportHyperlinks',
  data() {
    return {
      file: null,
      hyperlinks: [],
      droppedInstanceId: null,
      bulkUpload: false,
      bulkJsonFiles: [],
      currentFileIndex: 0,
      processedTemplates: [],
      templateOptions: [],
      selectedTemplateId: null,
      isDraggingOver: false,
    };
  },
  computed: {
    ...mapGetters('layout', [
      'selectedInstance',
    ]),
  },
  watch: {
    file(newVal) {
      if (!newVal) {
        return;
      }

      this.readContent();
      
      if (!this.selectedTemplateId) {
        if (this.bulkUpload) {
          const allTemplates = this.templateOptions.map(opt => opt.value);
          for (const templateId of allTemplates) {
            if (!this.processedTemplates.includes(templateId)) {
              this.selectedTemplateId = templateId;
              break;
            }
          }
        } else if (this.droppedInstanceId) {
          this.selectedTemplateId = this.droppedInstanceId;
        } else if (this.templateOptions.length > 0) {
          this.selectedTemplateId = this.templateOptions[0].value;
        }
      }
    }
  },  
  mounted() {
    importHyperlinksEventBroker.on(EVENTS.INIT, this.init);

    this.$nextTick(() => {
      this.setupModalDragDrop();
    });
  },
  methods: {
    init() {
      this.$bvModal.show('import-hyperlinks-modal');

      this.$nextTick(() => {
        // Load template options if not already loaded
        if (this.templateOptions.length === 0) {
          this.loadTemplateOptions();
        }
      });
    },
    handleClose() {
      this.file = null;
      this.hyperlinks = [];
      this.droppedInstanceId = null;
      this.bulkUpload = false;
      this.bulkJsonFiles = [];
      this.currentFileIndex = 0;
      this.processedTemplates = [];
      
      // Clear the global variables
      if (window.droppedJsonFiles) {
        window.droppedJsonFiles = null;
      }
      if (window.droppedInstanceId) {
        window.droppedInstanceId = null;
      }
      if (window.bulkJsonUpload !== undefined) {
        window.bulkJsonUpload = undefined;
      }
      
      this.$bvModal.hide('import-hyperlinks-modal');
    },
    handleOk() {
      if (this.hyperlinks.length === 0) {
        toastr.error('No hyperlinks found in the current file');
        return;
      }
      
      if (!this.selectedTemplateId) {
        toastr.error('Please select a template to apply the hyperlinks to');
        return;
      }
      
      // Use the selected template ID
      const instanceIds = [this.selectedTemplateId];
      
      importHyperlinksEventBroker.fire(EVENTS.DRAW, {
        hyperlinks: this.hyperlinks,
        instanceIds: instanceIds,
      });
      
      if (this.bulkUpload) {
        this.processedTemplates.push(this.selectedTemplateId);
      }
      
      if (this.bulkUpload && this.currentFileIndex < this.bulkJsonFiles.length - 1) {
        this.currentFileIndex++;
        this.file = this.bulkJsonFiles[this.currentFileIndex];
        
        this.selectedTemplateId = null;
        
        toastr.info(`Processing file ${this.currentFileIndex + 1} of ${this.bulkJsonFiles.length}`);
        
        return false;
      } else {
        if (this.bulkUpload) {
          toastr.success(`Successfully processed ${this.bulkJsonFiles.length} JSON files`);
        } else {
          toastr.success('Successfully imported hyperlinks');
        }
        
        this.$bvModal.hide('import-hyperlinks-modal');
      }
    },
    handleFileChange(event) {
      if (event.target.files.length === 0) {
        return;
      }

      this.file = event.target.files[0];
    },
    handleModalShown() {
      if (window.droppedJsonFiles && window.droppedJsonFiles.length > 0) {
        this.bulkUpload = window.bulkJsonUpload || false;
        this.bulkJsonFiles = window.droppedJsonFiles;
        this.droppedInstanceId = window.droppedInstanceId;
        
        this.currentFileIndex = 0;
        this.file = this.bulkJsonFiles[this.currentFileIndex];
        
        if (this.bulkUpload) {
          this.processedTemplates = [];
          
          this.loadTemplateOptions();
        }
      }
    },
    getAllTemplateInstances() {
      const instances = [];
      document.querySelectorAll('[data-instance]').forEach(el => {
        instances.push($(el).data('instance'));
      });
      return instances;
    },
    loadTemplateOptions() {
      this.templateOptions = [];
      const templates = document.querySelectorAll('[data-instance]');
      
      templates.forEach((template, index) => {
        const instanceId = $(template).data('instance');
        // Try to get template name if available, otherwise use index+1
        let templateName = '';
        try {
          const nameElement = template.querySelector('.template-name') || 
                              template.closest('.template-item').querySelector('.template-name-canvas');
          
          if (nameElement) {
            templateName = nameElement.textContent.trim();
          }
        } catch (e) {
          console.warn('Could not find template name', e);
        }
        
        this.templateOptions.push({
          value: instanceId,
          text: templateName || `Template ${index + 1}`,
          selected: instanceId === this.droppedInstanceId && index === 0
        });
      });
    },
    setupModalDragDrop() {
      this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
        if (modalId === 'import-hyperlinks-modal') {
          // Find the modal content element
          const modalContent = document.querySelector('#import-hyperlinks-modal .modal-content');
          if (!modalContent) return;
          
          // Set up the event listeners
          modalContent.addEventListener('dragover', this.handleModalDragOver);
          modalContent.addEventListener('dragleave', this.handleModalDragLeave);
          modalContent.addEventListener('drop', this.handleModalDrop);
          
          // Store a reference so we can remove the listeners later
          this.modalContentElement = modalContent;
        }
      });
      
      this.$root.$on('bv::modal::hidden', (bvEvent, modalId) => {
        if (modalId === 'import-hyperlinks-modal') {
          this.cleanupDragDropListeners();
        }
      });
    },
    handleModalDragOver(e) {
      e.preventDefault();
      e.stopPropagation();
      
      this.isDraggingOver = true;
    },
    handleModalDragLeave(e) {
      e.preventDefault();
      e.stopPropagation();
      
      const rect = this.modalContentElement.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX >= rect.right ||
        e.clientY < rect.top ||
        e.clientY >= rect.bottom
      ) {
        this.isDraggingOver = false;
      }
    },
    handleModalDrop(e) {
      e.preventDefault();
      e.stopPropagation();

      this.isDraggingOver = false;
      
      // Remove the visual indicator
      if (this.modalBodyElement) {
        this.modalBodyElement.classList.remove('drag-over');
      }
      
      const files = e.dataTransfer.files;
      if (!files.length) return;
      
      // Check if all files are JSON
      const allJsonFiles = Array.from(files).every(file => 
        file.type === 'application/json' || file.name.toLowerCase().endsWith('.json')
      );
      
      if (!allJsonFiles) {
        toastr.error('Please drop only JSON files');
        return;
      }
      
      // Handle multiple files
      if (files.length > 1) {
        this.bulkUpload = true;
        this.bulkJsonFiles = Array.from(files);
        this.currentFileIndex = 0;
        this.file = this.bulkJsonFiles[0];
        
        this.processedTemplates = [];
        
        this.loadTemplateOptions();
        
        toastr.info(`Processing ${files.length} JSON files.`);
      } else {
        this.bulkUpload = false;
        this.file = files[0];
        
        if (this.templateOptions.length === 0) {
          this.loadTemplateOptions();
        }
      }
    },
    cleanupDragDropListeners() {
      if (this.modalContentElement) {
        this.modalContentElement.removeEventListener('dragover', this.handleModalDragOver);
        this.modalContentElement.removeEventListener('dragleave', this.handleModalDragLeave);
        this.modalContentElement.removeEventListener('drop', this.handleModalDrop);
        this.modalContentElement = null;
      }
    },
    readContent() {
      let reader = new FileReader();

      reader.onload = (event) => {
        const content = JSON.parse(event.target.result);

        this.mapHyperlinks(content);
      };

      reader.readAsText(this.file);
    },
    mapHyperlinks(content) {
      this.hyperlinks = mapHyperlinks(content, MAPPER_TYPES.DEFAULT);
    }
  }
}
</script>
<style scoped>
.modal-body.drag-over {
  position: relative;
  border: 2px dashed #1c7964;
  background-color: rgba(28, 121, 100, 0.05);
}

.modal-body.drag-over::after {
  content: 'Drop JSON files here';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.5rem;
  color: #1c7964;
  font-weight: bold;
  pointer-events: none;
}

.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  border: 2px dashed #1c7964;
}

.drag-overlay-text {
  font-size: 1.5rem;
  color: #1c7964;
  font-weight: bold;
}

:deep(.modal-content) {
  position: relative;
}
</style>
